import { CognitoUser, AuthenticationHelper } from 'amazon-cognito-identity-js'
import { Credentials } from '@aws-amplify/core'

const EMAIL_MFA_CHALLENGE_TYPES = ['EMAIL_MFA', 'EMAIL_OTP']

const EMAIL_MFA_CODE_KEYS = {
  EMAIL_MFA: 'EMAIL_MFA_CODE',
  EMAIL_OTP: 'EMAIL_OTP_CODE',
}

let patchApplied = false

function isEmailMfaChallenge(challengeName) {
  return EMAIL_MFA_CHALLENGE_TYPES.indexOf(challengeName) !== -1
}

function getEmailMfaCodeKey(challengeName) {
  return EMAIL_MFA_CODE_KEYS[challengeName] || 'EMAIL_MFA_CODE'
}

function handleAuthChallengeSuccess(cognitoUser, dataAuthenticate, callback) {
  const challengeName = dataAuthenticate.ChallengeName

  if (challengeName === 'DEVICE_SRP_AUTH') {
    cognitoUser.getDeviceResponse(callback)
    return
  }

  if (!dataAuthenticate.AuthenticationResult) {
    const message = challengeName
      ? 'Unexpected authentication challenge: ' + challengeName
      : 'Invalid verification code'
    callback.onFailure(new Error(message))
    return
  }

  cognitoUser.signInUserSession = cognitoUser.getCognitoUserSession(
    dataAuthenticate.AuthenticationResult
  )
  cognitoUser.cacheTokens()

  if (dataAuthenticate.AuthenticationResult.NewDeviceMetadata == null) {
    callback.onSuccess(cognitoUser.signInUserSession)
    return
  }

  const authenticationHelper = new AuthenticationHelper(
    cognitoUser.pool.getUserPoolId().split('_')[1]
  )

  authenticationHelper.generateHashDevice(
    dataAuthenticate.AuthenticationResult.NewDeviceMetadata.DeviceGroupKey,
    dataAuthenticate.AuthenticationResult.NewDeviceMetadata.DeviceKey,
    function (errGenHash) {
      if (errGenHash) {
        callback.onFailure(errGenHash)
        return
      }

      const deviceSecretVerifierConfig = {
        Salt: Buffer.from(authenticationHelper.getSaltDevices(), 'hex').toString(
          'base64'
        ),
        PasswordVerifier: Buffer.from(
          authenticationHelper.getVerifierDevices(),
          'hex'
        ).toString('base64'),
      }

      cognitoUser.verifierDevices = deviceSecretVerifierConfig.PasswordVerifier
      cognitoUser.deviceGroupKey =
        dataAuthenticate.AuthenticationResult.NewDeviceMetadata.DeviceGroupKey
      cognitoUser.randomPassword = authenticationHelper.getRandomPassword()

      cognitoUser.client.request(
        'ConfirmDevice',
        {
          DeviceKey:
            dataAuthenticate.AuthenticationResult.NewDeviceMetadata.DeviceKey,
          AccessToken: cognitoUser.signInUserSession
            .getAccessToken()
            .getJwtToken(),
          DeviceSecretVerifierConfig: deviceSecretVerifierConfig,
          DeviceName:
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        },
        function (errConfirm, dataConfirm) {
          if (errConfirm) {
            callback.onFailure(errConfirm)
            return
          }

          cognitoUser.deviceKey =
            dataAuthenticate.AuthenticationResult.NewDeviceMetadata.DeviceKey
          cognitoUser.cacheDeviceKeyAndPassword()

          if (dataConfirm.UserConfirmationNecessary === true) {
            callback.onSuccess(
              cognitoUser.signInUserSession,
              dataConfirm.UserConfirmationNecessary
            )
            return
          }

          callback.onSuccess(cognitoUser.signInUserSession)
        }
      )
    }
  )
}

function applyCognitoEmailMfaPatch() {
  if (patchApplied) {
    return
  }
  patchApplied = true

  const originalAuthenticateUserInternal =
    CognitoUser.prototype.authenticateUserInternal
  CognitoUser.prototype.authenticateUserInternal = function authenticateUserInternalWithEmailMfa(
    dataAuthenticate,
    authenticationHelper,
    callback
  ) {
    const challengeName = dataAuthenticate.ChallengeName
    const challengeParameters = dataAuthenticate.ChallengeParameters

    if (isEmailMfaChallenge(challengeName)) {
      this.Session = dataAuthenticate.Session
      return callback.mfaRequired(challengeName, challengeParameters)
    }

    return originalAuthenticateUserInternal.call(
      this,
      dataAuthenticate,
      authenticationHelper,
      callback
    )
  }

  const originalSendMFACode = CognitoUser.prototype.sendMFACode
  CognitoUser.prototype.sendMFACode = function sendMFACodeWithEmailMfa(
    confirmationCode,
    callback,
    mfaType
  ) {
    const mfaTypeSelection = mfaType || 'SMS_MFA'

    if (!isEmailMfaChallenge(mfaTypeSelection)) {
      return originalSendMFACode.call(
        this,
        confirmationCode,
        callback,
        mfaType
      )
    }

    const cognitoUser = this
    const codeKey = getEmailMfaCodeKey(mfaTypeSelection)
    const challengeResponses = {
      USERNAME: this.username,
      [codeKey]: confirmationCode,
    }

    if (this.deviceKey != null) {
      challengeResponses.DEVICE_KEY = this.deviceKey
    }

    const jsonReq = {
      ChallengeName: mfaTypeSelection,
      ChallengeResponses: challengeResponses,
      ClientId: this.pool.getClientId(),
      Session: this.Session,
    }

    if (this.getUserContextData()) {
      jsonReq.UserContextData = this.getUserContextData()
    }

    this.client.request('RespondToAuthChallenge', jsonReq, function (
      err,
      dataAuthenticate
    ) {
      if (err) {
        return callback.onFailure(err)
      }

      handleAuthChallengeSuccess(cognitoUser, dataAuthenticate, callback)
    })
  }

  const originalSendMFASelectionAnswer =
    CognitoUser.prototype.sendMFASelectionAnswer
  CognitoUser.prototype.sendMFASelectionAnswer = function sendMFASelectionAnswerWithEmailMfa(
    answerChallenge,
    callback
  ) {
    if (!isEmailMfaChallenge(answerChallenge)) {
      return originalSendMFASelectionAnswer.call(
        this,
        answerChallenge,
        callback
      )
    }

    const self = this
    const challengeResponses = {
      USERNAME: this.username,
      ANSWER: answerChallenge,
    }

    const jsonReq = {
      ChallengeName: 'SELECT_MFA_TYPE',
      ChallengeResponses: challengeResponses,
      ClientId: this.pool.getClientId(),
      Session: this.Session,
    }

    if (this.getUserContextData()) {
      jsonReq.UserContextData = this.getUserContextData()
    }

    this.client.request('RespondToAuthChallenge', jsonReq, function (err, data) {
      if (err) {
        return callback.onFailure(err)
      }

      self.Session = data.Session

      const challengeName = data.ChallengeName || data.challengeName
      const challengeParameters =
        data.ChallengeParameters || data.challengeParameters

      if (isEmailMfaChallenge(challengeName)) {
        return callback.mfaRequired(challengeName, challengeParameters)
      }

      if (challengeName === 'SOFTWARE_TOKEN_MFA') {
        return callback.totpRequired(challengeName, challengeParameters)
      }

      if (challengeName === 'SMS_MFA') {
        return callback.mfaRequired(challengeName, challengeParameters)
      }

      return callback.onFailure(
        new Error('Unexpected MFA challenge: ' + (challengeName || 'unknown'))
      )
    })
  }
}

applyCognitoEmailMfaPatch()

export function isMfaChallenge(challengeName) {
  return (
    challengeName === 'SMS_MFA' ||
    challengeName === 'SOFTWARE_TOKEN_MFA' ||
    isEmailMfaChallenge(challengeName) ||
    (challengeName && challengeName.indexOf('MFA') !== -1)
  )
}

export function isMfaTypeSelectionChallenge(challengeName) {
  return challengeName === 'SELECT_MFA_TYPE'
}

export function getMfaMessageSuffix(challengeName) {
  if (challengeName === 'SMS_MFA') {
    return ' sent to your mobile device.'
  }
  if (challengeName === 'SOFTWARE_TOKEN_MFA') {
    return ' from your authenticator app.'
  }
  if (isEmailMfaChallenge(challengeName)) {
    return ' sent to your email.'
  }
  return ' sent to you.'
}

export function parseAvailableMfaTypes(challengeParam) {
  if (!challengeParam) {
    return ['EMAIL_MFA']
  }

  const raw =
    challengeParam.MFAS_CAN_CHOOSE ||
    challengeParam.MFAS_CAN_SELECT ||
    challengeParam.MFAS_CAN_SETUP

  if (!raw) {
    return ['EMAIL_MFA']
  }

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return Array.isArray(parsed) && parsed.length ? parsed : ['EMAIL_MFA']
  } catch (e) {
    return ['EMAIL_MFA']
  }
}

export function getMfaTypeLabel(mfaType) {
  switch (mfaType) {
    case 'EMAIL_MFA':
    case 'EMAIL_OTP':
      return 'Email'
    case 'SMS_MFA':
      return 'Text message'
    case 'SOFTWARE_TOKEN_MFA':
      return 'Authenticator app'
    default:
      return 'Verification code'
  }
}

export function selectMfaType(user, mfaType) {
  return new Promise((resolve, reject) => {
    user.sendMFASelectionAnswer(mfaType, {
      onFailure: reject,
      mfaRequired: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
      totpRequired: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
    })
  })
}

export function completeNewPasswordWithMfa(user, password, requiredAttributes) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(password, requiredAttributes, {
      onSuccess: session => {
        Credentials.clear()
          .then(() => Credentials.set(session, 'session'))
          .then(() => {
            user.signInUserSession = session
            resolve(user)
          })
          .catch(reject)
      },
      onFailure: reject,
      mfaRequired: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
      mfaSetup: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
      selectMFAType: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
      totpRequired: (challengeName, challengeParam) => {
        user.challengeName = challengeName
        user.challengeParam = challengeParam
        resolve(user)
      },
    })
  })
}
